import React, { useState } from "react";
import {
  Button,
  ButtonDropdown,
  Modal,
  FormField,
  Input,
  Box,
  SpaceBetween,
  Textarea,
} from "@amzn/awsui-components-react";
import { ButtonDropdownProps } from "@amzn/awsui-components-react/polaris/button-dropdown/interfaces";
import useSavedSearches, { SavedSearch } from "../hooks/useSavedSearches";

interface SavedSearchesManagerProps {
  getFilters: () => string;
  stage: string;
  onLoadSearch: (search: SavedSearch) => void;
}

const SAVE_ITEM_ID = "__save__";

export const SavedSearchesManager: React.FC<SavedSearchesManagerProps> = ({
  getFilters,
  stage,
  onLoadSearch,
}) => {
  const { searches, saveSearch, deleteSearch } = useSavedSearches();
  const [saveModalVisible, setSaveModalVisible] = useState(false);
  const [searchName, setSearchName] = useState("");
  const [searchDescription, setSearchDescription] = useState("");
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);

  const handleSave = () => {
    if (!searchName.trim()) {
      setError("Name is required");
      return;
    }

    setSaving(true);
    try {
      const filters = getFilters();
      saveSearch(searchName.trim(), searchDescription.trim(), stage, filters);
      setSaveModalVisible(false);
      setSearchName("");
      setSearchDescription("");
      setError("");
    } catch (e) {
      setError(e instanceof Error ? e.message : "Failed to save search");
    } finally {
      setSaving(false);
    }
  };

  const handleDismiss = () => {
    setSaveModalVisible(false);
    setSearchName("");
    setSearchDescription("");
    setError("");
  };

  const dropdownItems: ButtonDropdownProps.Items = [
    { id: SAVE_ITEM_ID, text: "Save current search…" },
    ...(searches.length > 0
      ? [
          {
            text: "Saved searches",
            items: searches.map((search) => ({
              id: search.id,
              text: search.name,
              description:
                search.description ||
                `${search.stage} — saved ${new Date(
                  search.createdAt
                ).toLocaleDateString()}`,
            })),
          },
        ]
      : []),
  ];

  return (
    <>
      <ButtonDropdown
        variant="icon"
        items={dropdownItems}
        ariaLabel="Saved searches"
        onItemClick={({ detail }) => {
          if (detail.id === SAVE_ITEM_ID) {
            setSaveModalVisible(true);
            return;
          }
          const search = searches.find((s) => s.id === detail.id);
          if (search) {
            onLoadSearch(search);
          }
        }}
      />

      <Modal
        visible={saveModalVisible}
        onDismiss={handleDismiss}
        header="Save current search"
        footer={
          <Box float="right">
            <SpaceBetween direction="horizontal" size="xs">
              <Button variant="link" onClick={handleDismiss}>
                Cancel
              </Button>
              <Button variant="primary" onClick={handleSave} loading={saving}>
                Save
              </Button>
            </SpaceBetween>
          </Box>
        }
      >
        <SpaceBetween size="m">
          <FormField label="Name" errorText={error}>
            <Input
              value={searchName}
              onChange={({ detail }) => {
                setSearchName(detail.value);
                setError("");
              }}
              placeholder="e.g. US Prod - Chargebacks this month"
              autoFocus
            />
          </FormField>
          <FormField label="Description" description="Optional">
            <Textarea
              value={searchDescription}
              onChange={({ detail }) => setSearchDescription(detail.value)}
              placeholder="What filters does this search represent?"
              rows={2}
            />
          </FormField>
          {searches.length > 0 && (
            <FormField label="Existing saved searches">
              <SpaceBetween size="xs">
                {searches.map((search) => (
                  <div
                    key={search.id}
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      padding: "4px 8px",
                      borderRadius: "4px",
                      background: "var(--color-background-container-content)",
                    }}
                  >
                    <span>
                      {search.name}{" "}
                      <span style={{ opacity: 0.6, fontSize: "12px" }}>
                        ({search.stage})
                      </span>
                    </span>
                    <Button
                      iconName="remove"
                      variant="icon"
                      onClick={() => {
                        deleteSearch(search.id);
                      }}
                      ariaLabel={`Delete ${search.name}`}
                    />
                  </div>
                ))}
              </SpaceBetween>
            </FormField>
          )}
        </SpaceBetween>
      </Modal>
    </>
  );
};
